import type { BranchInitEvent } from 'capacitor-branch-deep-links';
import { BranchDeepLinks } from 'capacitor-branch-deep-links';
import { Platform } from 'quasar';

import type * as branchTypes from 'src/types/branch';

/*
 * @note: Branch SDK lifecycle only. Attribution events belong to the unified tracking service
 * (`src/services/tracking.ts`, which owns the Branch/GTM platform branch); what lives here is the
 * SDK's own `init` / `initError` callbacks, which have no web counterpart. `initBranch` early-returns
 * off Capacitor, so importing this file on web is safe.
 */

/*
 * @note: The plugin leaves the `initError` payload untyped (`any`). Nothing guarantees its shape at
 * the TypeScript level — it is a plain object built on the native side (iOS `Plugin.swift`, Android
 * `BranchDeepLinks.java`) and crossing the bridge as JSON. Checked at runtime rather than asserted,
 * so a future SDK change that reshapes it falls through to the `console.error` branch below instead
 * of being silently misread.
 */
function isBranchInitErrorPayload(error: unknown): error is branchTypes.BranchInitErrorPayload {
  if (typeof error !== 'object' || error === null) return false;
  return typeof (error as Record<string, unknown>).error === 'string';
}

/*
 * @note: Why most Branch `initError`s are not an app bug. The SDK opens a session against
 * api3.branch.io on launch, on every return to the foreground, and again before a Branch event when
 * the last attempt failed, which is why an ordinary page shows up as the culprit. The native plugin
 * forwards only iOS's localized error text, never the error code, so the strings below are iOS's own
 * wording, in whichever of the two languages the shell was running:
 * - "bad URL" / "رابط غير صالح": reads like a broken deep link, but it is how a DNS ad-blocker
 *   sinking api3.branch.io surfaces, and Branch's own SDK detects it as exactly that.
 * - "The request timed out." / "انتهت مهلة الطلب.": a slow or dropping connection.
 * - "لم يتم العثور على خادم باسم المضيف المحدد.": DNS could not resolve the host at all.
 * - "Request contents were not transferred fast enough": the session request body stalled mid-upload.
 * None of them can be fixed here or in the Branch dashboard (a wrong Branch key would instead fail
 * for every user, with Branch's own English error). The user is unaffected; that device just sends
 * Branch no attribution data while its network blocks it. Every entry is a string iOS has actually
 * sent us — add the other language's wording when it shows up rather than guessing it.
 * Docs: https://developer.apple.com/documentation/foundation/urlerror/code
 * Sentry: https://ego-aqar.sentry.io/issues/146785720/
 * Issue: https://github.com/AbdulrhmanSobhyEgo/ego-travel-website/issues/105
 */
const DEVICE_NETWORK_INIT_ERRORS = [
  'bad URL',
  'رابط غير صالح',
  'The request timed out.',
  'انتهت مهلة الطلب.',
  'لم يتم العثور على خادم باسم المضيف المحدد.',
  'Request contents were not transferred fast enough',
];

export function initBranch(): void {
  if (!Platform.is.capacitor) return;

  // @note: `addListener` throws when the native plugin is not registered — an older shell, or a
  // build the plugin was dropped from — so it is caught rather than left to break app mount.
  try {
    void BranchDeepLinks.addListener('init', (event: BranchInitEvent) => {
      console.log('[Branch] Initialized successfully');
      console.log('[Branch] Referring params:', event.referringParams);

      if (event.referringParams?.['+clicked_branch_link']) {
        console.log('[Branch] User came from Branch deep link');
        // @fixme: the link's destination is logged and dropped — a Branch link opens the app on
        // whichever route it had last. `ego-ret-website`'s `src/services/deep-link.ts` reads Branch's
        // `$deeplink_path` / `+url` conventions against a route allowlist and is the pattern to port.
      }
    });

    void BranchDeepLinks.addListener('initError', (error: unknown) => {
      // @note: Stringified because Sentry's console capture renders an object argument as
      // `[object Object]` in the event title, which hid every reason listed above behind one
      // unreadable issue. The payload is a plain object from the native bridge, so it is JSON-safe.
      const payload = JSON.stringify(error);
      // @note: A device-network failure stays a breadcrumb (`console.log`), not a Sentry error:
      // Branch retries on its own and there is nothing here to fix. Anything else — a rejected key,
      // a reshaped payload — still reports.
      if (isBranchInitErrorPayload(error) && DEVICE_NETWORK_INIT_ERRORS.includes(error.error)) {
        console.log('[Branch] Initialization failed on the device network:', payload);
        return;
      }
      console.error('[Branch] Initialization error:', payload);
    });

    console.log('[Branch] Listeners registered');
  } catch (error) {
    console.error('[Branch] Plugin not available:', error);
  }
}
